import React, { useEffect, useState } from 'react';
import { FaEnvelope, FaMapMarkerAlt, FaPhoneAlt, FaPaperPlane, FaUser, FaComment, FaBriefcase, FaLinkedin, FaInstagram, FaWhatsapp } from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import countryCodes from '../data/countryCodes';
import tempEmailDomains from '../data/tempEmailDomains';

const isTemporaryEmail = (email) => {
  if (!email) return false;
  const domain = email.toLowerCase().split('@')[1];
  if (!domain) return false;
  return tempEmailDomains.includes(domain);
};

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+91',
    phone: '',
    service: '',
    message: ''
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) entry.target.classList.add('visible');
      });
    }, { threshold: 0.1 });

    document.querySelectorAll('.fade-in').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(s => ({ ...s, [name]: value }));

    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));

    if (name === 'email' && value && isTemporaryEmail(value)) {
      setErrors(prev => ({ ...prev, email: 'Temporary emails are not allowed' }));
    }
  };

  const validate = () => {
    const errs = {};
    if (!formData.name) errs.name = 'Name required';
    if (!formData.email) errs.email = 'Email required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errs.email = 'Invalid email';
    else if (isTemporaryEmail(formData.email)) errs.email = 'Temporary emails are not allowed';
    if (!formData.service) errs.service = 'Select a service';
    if (!formData.message) errs.message = 'Message required';

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ✉️ Mailto submit (no backend)
  const submit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const subject = encodeURIComponent("New Inquiry from Website");

    const body = encodeURIComponent(
`Name: ${formData.name}
Email: ${formData.email}
Phone: ${formData.countryCode} ${formData.phone}
Service: ${formData.service}

Message:
${formData.message}`
    );

    window.location.href = `mailto:info@syntax2scale.com?subject=${subject}&body=${body}`;
  };

  return (
    <div>
      <section className="section" style={{ paddingTop: '8rem' }}>
        <h1 className="section-title fade-in">Contact Us</h1>
        <p className="section-subtitle fade-in">Let's talk about your project.</p>

        <div className="contact-container">
          <form className="contact-form fade-in" onSubmit={submit}>
            <div className="form-group">
              <label className="form-label"><FaUser /> Name</label>
              <input name="name" className={`form-control ${errors.name ? 'error' : ''}`} value={formData.name} onChange={handleChange} />
              <div className="form-error">{errors.name || ''}</div>
            </div>

            <div className="form-group">
              <label className="form-label"><FaEnvelope /> Email</label>
              <input name="email" className={`form-control ${errors.email ? 'error' : ''}`} value={formData.email} onChange={handleChange} />
              <div className="form-error">{errors.email || ''}</div>
            </div>

            <div className="form-group">
              <label className="form-label"><FaPhoneAlt /> Phone (optional)</label>
              <div className="phone-input-group">
                <select name="countryCode" className="form-control country-code-select" value={formData.countryCode} onChange={handleChange}>
                  {countryCodes.map((c, idx) => (
                    <option key={idx} value={c.code}>{c.name} ({c.code})</option>
                  ))}
                </select>
                <input type="tel" name="phone" className="form-control phone-number-input" placeholder="Enter phone number" value={formData.phone} onChange={handleChange} />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label"><FaBriefcase /> Service</label>
              <select name="service" className={`form-control ${errors.service ? 'error' : ''}`} value={formData.service} onChange={handleChange}>
                <option value="">Select a Service</option>
                <option value="general">General Inquiry</option>
                <option value="web">Web Development</option>
                <option value="app">App Development</option>
                <option value="cloud">Cloud Solutions</option>
                <option value="ai">AI/ML Solutions</option>
                <option value="content">Content Creation</option>
                <option value="consulting">Business Consultancy</option>
                <option value="marketing">Digital Marketing</option>
                <option value="other">Other</option>
              </select>
              <div className="form-error">{errors.service || ''}</div>
            </div>

            <div className="form-group">
              <label className="form-label"><FaComment /> Message</label>
              <textarea name="message" className={`form-control ${errors.message ? 'error' : ''}`} placeholder="Tell us about your project..." value={formData.message} onChange={handleChange}></textarea>
              <div className="form-error">{errors.message || ''}</div>
            </div>

            <button type="submit" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }}>
              <FaPaperPlane /> Send Message
            </button>
          </form>

          <div className="contact-right fade-in">
            <div className="contact-info-card">
              <h3>Contact Information</h3>
              <div className="contact-item"><span className="contact-icon"><FaEnvelope /></span><span>info@syntax2scale.com</span></div>
              <div className="contact-item"><span className="contact-icon"><FaWhatsapp /></span><span>+91 92747 66776</span></div>
              <div className="contact-item"><span className="contact-icon"><FaMapMarkerAlt /></span><span>Ahmedabad, India</span></div>
            </div>

            <div className="contact-info-card">
              <h3>Follow Us</h3>
              <div className="social-links">
                <a href="https://www.linkedin.com/company/syntax2scale/" className="social-link" target="_blank" rel="noreferrer"><FaLinkedin /></a>
                <a href="https://x.com/syntax2scale" className="social-link" target="_blank" rel="noreferrer"><FaXTwitter /></a>
                <a href="https://www.instagram.com/syntax2scale" className="social-link" target="_blank" rel="noreferrer"><FaInstagram /></a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}